import { isSameDay } from 'date-fns'
import type { TFunction } from 'i18next'
import Personnummer from 'personnummer'
import { type CurrencyCode } from '@/services/graphql/generated'
import { type IsoLocale } from '@/utils/l10n/types'

type MoneyFormatOptions = { locale: IsoLocale }

export type Money = {
  amount: number
  currencyCode: CurrencyCode
}

const formatMoney = (money: Money, options: MoneyFormatOptions): string => {
  return (
    new Intl.NumberFormat(options.locale, {
      style: 'currency',
      currency: money.currencyCode,
      // Same strategy used by the API to round prices
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      roundingMode: 'halfEven',
    })
      .format(money.amount)
      // Hacky solution: API returns prices with dot as decimal separator but current support locales will use comma
      .replace(',', '.')
  )
}

const formatMonthlyPrice = (price: Money, options: MoneyFormatOptions & { t: TFunction }): string => {
  const displayAmount = formatMoney(price, options)
  return options.t('MONTHLY_PRICE', {
    displayAmount,
    defaultValue: `MONTHLY_PRICE ${displayAmount}`,
  })
}

const formatAnnualReduction = (price: Money, options: MoneyFormatOptions & { t: TFunction }): string => {
  const displayAmount = formatMoney({ ...price, amount: price.amount * 12 }, options)
  return options.t('ANNUAL_REDUCTION', {
    displayAmount,
    defaultValue: `ANNUAL_REDUCTION ${displayAmount}`,
  })
}

type DateFormatOptions = { locale: IsoLocale } & { t: TFunction }

const formatDateWithDots = (date: Date): string => {
  // FYI: dates are formatted the same across locales
  const result = date.toLocaleDateString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Europe/Stockholm',
  })

  return result.replace(/-/g, '.')
}

const formatDateFromNow = (date: Date, options: DateFormatOptions): string => {
  const today = new Date()

  if (isSameDay(date, today)) {
    return options.t('DATE_SAME_DAY')
  }

  return formatDateWithDots(date)
}

const formatTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

const formatSsn = (ssn: string): string => {
  try {
    const longFormat = Personnummer.parse(ssn).format(true)
    // replace the last 4 digits with asterisks
    return longFormat.replace(/(\d{4})$/, '-****')
  } catch (error) {
    console.warn(`Could not format SSN: ${ssn}`, error)
    return ssn
  }
}

const formatCarRegistrationNumber = (regNumber: string): string => {
  // add space after 3rd character
  return regNumber.toUpperCase().replace(/(.{3})/, '$1 ')
}

export const formatMaskedStreetAddress = (address: string): string => {
  // First we need to clean trailing abbreviations from the address return from SPAR
  let cleaned = address

  // Remove trailing "lgh <number>"
  cleaned = cleaned.replace(/\s+lgh\s+\d+$/i, '')

  // Remove trailing "<number>/<number> Tr"
  cleaned = cleaned.replace(/\s+\d+\/\d+\s*tr$/i, '')

  // Remove trailing "<number> Tr"
  cleaned = cleaned.replace(/\s+\d+\s*tr$/i, '')

  // Remove trailing "U2", "U22", etc.
  cleaned = cleaned.replace(/\s+U\d{1,2}$/i, '')

  // Remove trailing "Bv"
  cleaned = cleaned.replace(/\s+Bv$/i, '')

  // Normalize spacing
  cleaned = cleaned.trim().replace(/\s{2,}/g, ' ')

  // Step 2: Mask remaining words (except short ones or digits)
  return cleaned
    .split(' ')
    .map((word) => {
      if (/^\d/.test(word) || /^\d+-\d+$/.test(word)) {
        // keep numbers and ranges
        return word
      }
      if (word.length <= 2) {
        // preserve short suffixes like "H", "A", etc.
        return word
      }
      // mask the middle of the words with *
      return word[0] + '*'.repeat(word.length - 2) + word[word.length - 1]
    })
    .join(' ')
}

export const formatMaskedZipCode = (zipCode: string): string => {
  return zipCode.replace(/(\d{3})(\d{2})/, '*** $2')
}

const formatZipCode = (value: string): string => {
  return value.replace(/(\d{3})(\d{2})/, '$1 $2')
}

const formatNumberGrouping = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, { useGrouping: true }).format(value)

type FormatterOptions = MoneyFormatOptions & { t: TFunction }

export class Formatter {
  constructor(public options: FormatterOptions) {
    // Has to be assigned explicitly for Storybook compatibility, looks like it messes up TS constructor properties
    this.options = options
  }

  public money = (money: Money) => formatMoney(money, this.options)
  public monthlyPrice = (price: Money) => formatMonthlyPrice(price, this.options)
  public annualReduction = (price: Money) => formatAnnualReduction(price, this.options)
  public fromNow = (date: Date) => formatDateFromNow(date, this.options)
  public dateWithDots = (date: Date) => formatDateWithDots(date)
  public dateFull = (date: Date, options?: { hideYear?: boolean; abbreviateMonth?: boolean }) => {
    const formattedDate = date.toLocaleDateString(this.options.locale, {
      year: options?.hideYear ? undefined : 'numeric',
      month: options?.abbreviateMonth ? 'short' : 'long',
      day: 'numeric',
      timeZone: 'Europe/Stockholm',
    })

    // Removes '.' used for abbreviations
    if (options?.abbreviateMonth) {
      return formattedDate.replace('.', '')
    }

    return formattedDate
  }

  public titleCase = (str: string) => formatTitleCase(str)
  public ssn = formatSsn
  public maskedZipCode = formatMaskedZipCode
  public maskedStreetAddress = formatMaskedStreetAddress
  public carRegistrationNumber = formatCarRegistrationNumber
  public zipCode = formatZipCode
  public numberGrouping = (value: number) => formatNumberGrouping(value, this.options.locale)
}
